
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import ProductCard from './utils/ProductCard.vue';
import ProductService from '../services/ProductService';
import { ProductSearchResultProduct } from '../models/Product';
import isEqual from 'lodash/isEqual';
import ProductListFilterService from '@/services/ProductListFilterService';
import { FilterModel } from '@/models/Filter';

// @ts-ignore
const UIkit = (window as any).UIkit;

@Component({
    name: 'ProductSlider',
    components: {
        ProductCard,
    },
})
export default class ProductSlider extends Vue {
    @Prop({ type: Boolean, required: false, default: true }) private showDottedNav: boolean;
    @Prop({ type: Boolean, required: false, default: true }) private showArrows: boolean;
    @Prop({ type: Array, required: false, default: (): string[] => [] }) private productIds: string[];
    @Prop({ type: Object, required: false }) private query: FilterModel;
    @Prop({ type: Boolean, required: false, default: true }) private fullWidth: boolean;
    @Prop({ type: Boolean, required: false, default: false }) private asSection: boolean;
    @Prop({ type: Boolean, required: false, default: false }) private finite: boolean;

    private ps: ProductService = new ProductService();
    private pfs: ProductListFilterService = new ProductListFilterService();
    private products: ProductSearchResultProduct[] = [];
    private ready: boolean = false;
    private slider: any = null;
    private loaded = false;
    private sliderElem: HTMLElement | null = null;

    private maxRenderIndex: number = 10;
    private minRenderIndex = 0;
    
    private async created() {
        await this.fetchProducts();
    }

    @Watch('productIds')
    private async updateProducts(n: any, o: any) {
        if (!this.ready || isEqual(n, o)) return;      
        await this.fetchProducts();
    }

    private beforeDestroy() {
        this.destroySlider();
    }

    private initSlider() {
        this.destroySlider(); 
        this.sliderElem = this.$refs.sliderContainer as HTMLElement;
        
        if (this.sliderElem) {
            if (!this.finite) {
                this.minRenderIndex = 0;
                this.maxRenderIndex = this.products.length;
            }

            this.$nextTick(() => {
                this.slider = UIkit.slider(this.sliderElem, {
                    sets: true,
                    finite: this.finite
                });
                
                UIkit.util.on(this.sliderElem, 'itemshow', this.handleItemShow);
                this.loaded = true;
            });
        }
    }

    private handleItemShow() {
        if (!this.$refs.sliderContainer) return;

        if (!this.finite) return;
        
        // Load and unload "batches" of ProductCards
        requestAnimationFrame(() => {
            const sliderElem = this.$refs.sliderContainer as Element;
            const items = Array.from(sliderElem.querySelectorAll('.uk-slider-items-item'));
            const activeIndex = items.findIndex(el => el.classList.contains('uk-active'));
            if (activeIndex === -1) return;

            const backwardBuffer = 6;
            const forwardBuffer = 10;

            const newMin = Math.max(0, activeIndex - backwardBuffer);
            const newMax = Math.min(this.products.length, activeIndex + forwardBuffer);

            if (this.minRenderIndex !== newMin || this.maxRenderIndex !== newMax) {
                this.minRenderIndex = newMin;
                this.maxRenderIndex = newMax;
            }
        });
    }
    

    private destroySlider() {
        if (this.sliderElem) {
             // Clean up the event listener to prevent memory leaks
             UIkit.util.off(this.sliderElem, 'itemshow', this.handleItemShow);
             this.sliderElem = null;
        }

        if (this.slider && typeof this.slider.$destroy === 'function') {
            this.slider.$destroy(true);
            this.slider = null;
        }
    }
    

    private async fetchProducts() {
        this.loaded = false; // Hide while loading to prevent UIkit errors
        this.minRenderIndex = 0;
        this.maxRenderIndex = 12;

        if (this.productIds.length > 0 && !this.query) {
            this.products = await this.ps.getProductsById(this.productIds);
        } else if (this.query) {
            const res = await this.pfs.getFilterResult(this.query);
            this.products = res.hits?.filter((o) => o.metadata?.product)?.map((o) => o.metadata?.product) ?? [];
        } else {
            this.products = [];
        }

        this.ready = true;

        // Wait for Vue to update the DOM (resolving the v-if="products.length")
        if (this.products.length > 0) {
            await this.$nextTick(); 
            this.initSlider();
        } else {
            this.loaded = true;
            this.destroySlider();
        }
    }

}
